import React from "react";
import HelpIcon from "../shared/HelpIcon";

const palette = {
  border: "#86B6F6",
  ink: "#0B2230",
  accent: "#176B87",
};

const HeaderCell: React.FC<{
  label: string;
  helpTitle: string;
  helpBody: React.ReactNode;
  align?: "left" | "right" | "center";
  minWidth?: number;
}> = ({ label, helpTitle, helpBody, align = "left", minWidth }) => (
  <th
    style={{
      textAlign: align,
      padding: "6px 8px",
      borderBottom: `1px solid ${palette.border}`,
      background: "rgba(180,212,255,0.40)",
      position: "sticky" as any,
      top: 0,
      zIndex: 1,
      minWidth,
      whiteSpace: "nowrap",
    }}
  >
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontWeight: 950 }}>{label}</span>
      <HelpIcon
        title={helpTitle}
        body={helpBody}
        ariaLabel={`${label} column help`}
      />
    </div>
  </th>
);

export type OpponentRow = {
  opponent: string;
  played: number;
  wins: number;
  losses: number;
  draws: number;
  pf: number;
  pa: number;
  gd: number;
  winPct: number;
};

interface OpponentsTableProps {
  rows: OpponentRow[];
}

const OpponentsTable: React.FC<OpponentsTableProps> = ({ rows }) => (
  <div style={{ overflowX: "auto" }}>
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 13,
        color: palette.ink,
      }}
    >
      <thead>
        <tr>
          <HeaderCell
            label="Opponent"
            helpTitle="Opponent"
            helpBody={
              <>
                <div>The opposing team.</div>
                <div style={{ marginTop: 6 }}>
                  All metrics in the row are computed only for matches vs this
                  opponent.
                </div>
              </>
            }
            minWidth={160}
          />
          <HeaderCell
            label="P"
            helpTitle="P — Played"
            helpBody={
              <>
                <div>Total matches played against this opponent.</div>
                <div style={{ marginTop: 6 }}>
                  More matches usually means the percentages are more reliable.
                </div>
              </>
            }
            align="right"
          />
          <HeaderCell
            label="W"
            helpTitle="W — Wins"
            helpBody={<div>Number of wins against this opponent.</div>}
            align="right"
          />
          <HeaderCell
            label="D"
            helpTitle="D — Draws"
            helpBody={<div>Number of drawn matches against this opponent.</div>}
            align="right"
          />
          <HeaderCell
            label="L"
            helpTitle="L — Losses"
            helpBody={<div>Number of losses against this opponent.</div>}
            align="right"
          />
          <HeaderCell
            label="PF"
            helpTitle="PF — Points For"
            helpBody={
              <>
                <div>
                  Total points scored by the selected team against this opponent.
                </div>
                <div style={{ marginTop: 6 }}>
                  Higher PF means you tend to score more on them.
                </div>
              </>
            }
            align="right"
          />
          <HeaderCell
            label="PA"
            helpTitle="PA — Points Against"
            helpBody={
              <>
                <div>
                  Total points conceded by the selected team against this opponent.
                </div>
                <div style={{ marginTop: 6 }}>
                  Higher PA means this opponent scores more on you.
                </div>
              </>
            }
            align="right"
          />
          <HeaderCell
            label="GD"
            helpTitle="GD — Game Difference"
            helpBody={
              <>
                <div>
                  Goal/points difference: <b>GD = PF − PA</b>.
                </div>
                <div style={{ marginTop: 6 }}>
                  Positive → you outscore them overall. Negative → they outscore
                  you.
                </div>
              </>
            }
            align="right"
          />
          <HeaderCell
            label="Win%"
            helpTitle="Win% — Win Rate"
            helpBody={
              <>
                <div>
                  Percentage of matches won vs this opponent: <b>W / P</b>.
                </div>
                <div style={{ marginTop: 6 }}>
                  Use together with P: a 100% win rate over 1 game is less
                  meaningful than 70% over 20 games.
                </div>
              </>
            }
            align="right"
          />
        </tr>
      </thead>

      <tbody>
        {rows.map((r) => (
          <tr key={r.opponent}>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                whiteSpace: "nowrap",
              }}
            >
              {r.opponent}
            </td>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                textAlign: "right",
              }}
            >
              {r.played}
            </td>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                textAlign: "right",
              }}
            >
              {r.wins}
            </td>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                textAlign: "right",
              }}
            >
              {r.draws}
            </td>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                textAlign: "right",
              }}
            >
              {r.losses}
            </td>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                textAlign: "right",
              }}
            >
              {r.pf}
            </td>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                textAlign: "right",
              }}
            >
              {r.pa}
            </td>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                textAlign: "right",
                fontWeight: 900,
                color:
                  r.gd > 0
                    ? palette.accent
                    : r.gd < 0
                    ? "rgba(138,31,31,0.95)"
                    : palette.ink,
              }}
            >
              {r.gd}
            </td>
            <td
              style={{
                padding: "6px 8px",
                borderBottom: `1px solid rgba(134,182,246,0.45)`,
                textAlign: "right",
              }}
            >
              {r.winPct.toFixed(1)}%
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default OpponentsTable;
